
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const testimonials = [
  {
    quote: 'I stopped doom-scrolling within 10 days. My brain feels like it belongs to me again.',
    name: 'Sarah K.',
    role: 'Designer',
    avatar: 'ri-user-3-line',
  },
  {
    quote: 'The dopamine reset protocol changed everything. I wake up motivated without needing my phone first.',
    name: 'Marcus T.',
    role: 'Founder',
    avatar: 'ri-user-3-line',
  },
  {
    quote: 'I finally understand what a calm baseline feels like. This is not meditation or mind thing, it is truly rewiring.',
    name: 'Elena R.',
    role: 'Therapist',
    avatar: 'ri-user-3-line',
  },
];

export default function SocialProof() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 bg-white overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-violet/12 to-transparent"></div>

      {/* Subtle neural background */}
      <NeuralNetworkBg
        color="192,109,204"
        nodeCount={18}
        opacity={0.05}
        speed={0.3}
        variant="light"
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-teal/5 border border-brand-teal/8 mb-8">
            <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse"></div>
            <span className="text-xs font-semibold text-brand-teal uppercase tracking-widest">
              Voices
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-tight">
            They felt it <span className="gradient-text">too. The voices of the human lead protocols Brain Wash is built on.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, index) => (
            <div
              key={index}
              className={`group relative p-8 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white border border-gray-100/80 hover:border-brand-pink/15 hover:shadow-xl hover:shadow-brand-pink/8 transition-all duration-500 cursor-pointer ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="absolute top-6 right-6 text-4xl gradient-text opacity-15 font-serif">
                &ldquo;
              </div>
              <p className="text-sm text-gray-500 leading-relaxed mb-8 relative z-10">
                {t.quote}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-orange/8 via-brand-pink/8 to-brand-violet/8">
                  <i className={`${t.avatar} text-lg text-brand-pink`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
